// The body manifest: every registry row listed, and a jump to any of them.
//
// The orrery's own answer to "where is Ceres" used to be dragging and zooming
// the void until a two-pixel dot came under the cursor, and the opening fit
// reaches Neptune, so most of the registry projects inside Sol's own pick disc
// there. This panel is the other route: a row per body whether or not it is
// drawn, on screen, or inside the fit, and a click that goes through the same
// focus path the double-click aim and `?body=` take.

import { escapeHtml } from '../html.ts';
import type { BodyClassId, BodyRow } from '../world/types';

/** What the honesty overlay and the credit label already say about a row. */
export interface ManifestReading {
  /** The availability chip, in the words the overlay's reach row uses. */
  chip: string;
  /** The data floor: the rung word the descent stops at, and the level. */
  floor: string;
  /** The data product and its credit, as the credit label reads them. */
  credit: string;
}

export interface BodyManifestConfig {
  /** Where the panel mounts. */
  host: HTMLElement;
  /** The registry, in its own order. Empty until the fetch lands. */
  rows: () => readonly BodyRow[];
  /** The body in focus, so the open row can say which one it is. */
  focusedBodyId: () => string | null;
  /** Whether the panel belongs on screen at the rung the camera is on. */
  visible: () => boolean;
  /** Jump the camera. The SAME path the double-click aim and `?body=` take. */
  focus: (bodyId: string) => void;
  /** The derived facts, from the module that owns them. */
  reading: (row: BodyRow) => ManifestReading;
}

export interface BodyManifestHandle {
  update(): void;
  /**
   * `descent-dojo --manifest --inject manifest-miswire`: exchange two rows'
   * jump targets, and render the first of the pair with the second's label.
   *
   * The one control this panel has, covering both bars a criterion can read:
   * a row's label and its jump come from the same registry id, so wiring the
   * jump to the other row while leaving the label as it was would only ever
   * be caught by `jump`. Rendering the label wrong too gives `label` its own
   * failing case in the same run, off the same swap.
   *
   * `paints` is how many times the DOM was actually rewritten: the arm's own
   * stability bar reads it before and after a run of zero-delta wheel ticks,
   * which must not move it.
   */
  setDebug(next: { swap?: boolean }): { swap: readonly string[] | null; paints: number };
  dispose(): void;
}

/**
 * Folded state, so a reader's own fold survives a repaint and a remount.
 *
 * `.v2`: the list now starts open for everyone, so a v1 value (folded by an
 * older default) is never read back as a fold the reader chose.
 */
const STORE_KEY = 'atlas.manifest.v2';

/**
 * The class word, from the registry's own id.
 *
 * A table and not `id.replace(/^\d_/, '')`: that yields "gas_atmosphere",
 * which is the field name and not what the body is. An id the table has no row
 * for still reads, because a registry that grows a class must not blank a row.
 */
const CLASS_WORD: Readonly<Record<BodyClassId, string>> = {
  '0_star': 'star',
  '1_terrestrial': 'terrestrial',
  '2_icy_moon': 'icy moon',
  '3_asteroid': 'asteroid',
  '4_gas_atmosphere': 'gas giant',
};

const AU_M = 1.495978707e11;

function classWord(row: BodyRow): string {
  return CLASS_WORD[row.class] ?? row.class.replace(/^\d_/, '').replace(/_/g, ' ');
}

/** Radius in km, with enough figures to tell Phobos from Deimos. */
function radiusText(row: BodyRow): string {
  const km = row.radius_m / 1000;
  return `${km < 100 ? km.toFixed(1) : Math.round(km).toLocaleString()} km`;
}

/**
 * The solar day, which is the one a person standing on the body would count.
 *
 * `rotation.solar_day_s` is null on rows nobody has fitted one for, and the
 * sidereal period is NOT it, so the row says which of the two it is showing
 * rather than passing one off as the other.
 */
function dayText(row: BodyRow): string {
  const s = row.rotation.solar_day_s;
  if (s !== null) return `${(s / 3600).toFixed(2)} h`;
  const sid = row.rotation.period_s;
  if (!Number.isFinite(sid) || sid === 0) return 'not fitted';
  return `${(Math.abs(sid) / 3600).toFixed(2)} h sidereal`;
}

/** AU for a heliocentric row, km for a satellite: its own orbit's scale. */
function orbitText(row: BodyRow): string {
  const o = row.orbit;
  if (o === null) return 'at the frame origin';
  if (!o.parent_relative) return `${o.a_au.toFixed(3)} AU`;
  return `${Math.round((o.a_au * AU_M) / 1000).toLocaleString()} km`;
}

interface Node {
  row: BodyRow;
  children: Node[];
}

/**
 * Sol, then the heliocentric rows by semi-major axis with each primary's
 * satellites under it.
 *
 * The root is the row whose `parent` is null rather than a row called "sol":
 * nothing here may assume an id, a name or a count. A row whose parent is not
 * in the registry, or whose parent chain loops back on itself, is listed at
 * the top level rather than dropped, because a body missing from the list is
 * the exact failure this panel exists to fix.
 */
function tree(rows: readonly BodyRow[]): Node[] {
  const byId = new Map<string, Node>();
  for (const row of rows) byId.set(row.id, { row, children: [] });
  const parentOf = (n: Node): Node | null => {
    if (n.row.parent === null) return null;
    const p = byId.get(n.row.parent);
    return p === undefined || p === n ? null : p;
  };
  // A cycle (a -> b -> a, or longer) has no null at the end of its chain, so
  // walking each node's own ancestors and stopping the moment one repeats
  // finds every row on a cycle without an unbounded loop - the repeat is
  // necessarily the walk's own starting node revisited, or an ancestor
  // shared with an earlier node's walk, either way a member of the cycle.
  const cyclic = new Set<Node>();
  for (const start of byId.values()) {
    const seen = new Set<Node>([start]);
    for (let p = parentOf(start); p !== null; p = parentOf(p)) {
      if (seen.has(p)) { cyclic.add(p); break; }
      seen.add(p);
    }
  }
  const roots: Node[] = [];
  for (const node of byId.values()) {
    const parent = parentOf(node);
    if (parent === null || cyclic.has(node)) roots.push(node);
    else parent.children.push(node);
  }
  const byAxis = (a: Node, b: Node): number =>
    (a.row.orbit?.a_au ?? 0) - (b.row.orbit?.a_au ?? 0);
  // The frame origin first whatever the registry's order is, then by distance.
  roots.sort((a, b) => {
    const ao = a.row.orbit === null ? 0 : 1;
    const bo = b.row.orbit === null ? 0 : 1;
    return ao !== bo ? ao - bo : byAxis(a, b);
  });
  for (const node of byId.values()) node.children.sort(byAxis);
  return roots;
}

/** Everything about the registry that does not change until it reloads. */
interface RowCache {
  /** The exact array `rows()` returned when this was built. */
  rowsRef: readonly BodyRow[];
  tree: Node[];
  /** Every row in draw order, roots and satellites alike. */
  flatIds: string[];
  /** One `reading()` call per row, made here and never again. */
  readingById: Map<string, ManifestReading>;
}

export function createBodyManifest(config: BodyManifestConfig): BodyManifestHandle {
  const { host, rows, focusedBodyId, visible, focus, reading } = config;

  const stored = ((): { open: boolean; rows: string[] } => {
    try {
      const raw = JSON.parse(localStorage.getItem(STORE_KEY) ?? 'null') as unknown;
      if (raw === null || typeof raw !== 'object') return { open: true, rows: [] };
      const o = raw as { open?: unknown; rows?: unknown };
      return {
        open: o.open !== false,
        rows: Array.isArray(o.rows) ? o.rows.filter((r): r is string => typeof r === 'string') : [],
      };
    } catch {
      // Private mode, or a value from an older shape: the panel opens open.
      return { open: true, rows: [] };
    }
  })();

  let open = stored.open;
  const expanded = new Set<string>(stored.rows);

  function remember(): void {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify({ open, rows: [...expanded] }));
    } catch {
      /* private mode; the fold just will not persist */
    }
  }

  const el = document.createElement('div');
  el.className = 'atlas-manifest';
  host.appendChild(el);

  /** The last markup written, so a repaint that changes nothing does nothing. */
  let painted: string | null = null;
  /** The dynamic key `painted` was built from; see `dynamicKey`. */
  let paintedKey: string | null = null;
  /** How many times `el.innerHTML` has actually been rewritten. */
  let paints = 0;
  /** The two ids the miswire control exchanges, or null. */
  let swap: readonly [string, string] | null = null;

  let cache: RowCache | null = null;

  /** (Re)builds the registry-derived cache if the registry itself has moved. */
  function ensureCache(): RowCache {
    const all = rows();
    if (cache !== null && cache.rowsRef === all) return cache;
    const builtTree = tree(all);
    const flatIds: string[] = [];
    const readingById = new Map<string, ManifestReading>();
    const walk = (n: Node): void => {
      flatIds.push(n.row.id);
      readingById.set(n.row.id, reading(n.row));
      for (const c of n.children) walk(c);
    };
    for (const r of builtTree) walk(r);
    cache = { rowsRef: all, tree: builtTree, flatIds, readingById };
    return cache;
  }

  /** Every row in the order the panel draws them, roots and satellites alike. */
  function flat(): string[] {
    return ensureCache().flatIds;
  }

  /**
   * Everything a repaint could actually change, folded into one string.
   *
   * Excludes anything `reading()` covers (name, chip, data floor, parentage):
   * those come off the registry alone and are cached once in `ensureCache`.
   * `all.length` stands in for "the registry just landed", since that is a
   * fresh array from `rows()` and `ensureCache` already keys on it directly -
   * it is here so an EMPTY-to-loaded transition also shows up as a key change
   * for a reader who only compares this string.
   */
  function dynamicKey(all: readonly BodyRow[]): string {
    return `${all.length}|${open ? 1 : 0}|${focusedBodyId() ?? ''}|${[...expanded].sort().join(',')}`
      + `|${swap ? swap.join(',') : ''}`;
  }

  function detail(row: BodyRow, depth: number, r: ManifestReading): string {
    const primary = row.parent === null
      ? 'none - this is the frame origin'
      : rows().find((x) => x.id === row.parent)?.display_name ?? row.parent;
    const line = (k: string, v: string): string =>
      `<div class="atlas-mf-fact"><span>${k}</span><span>${escapeHtml(v)}</span></div>`;
    return `<div class="atlas-mf-detail" id="mf-detail-${escapeHtml(row.id)}"
      style="--mf-depth:${depth}">
      ${line('radius', radiusText(row))}
      ${line('solar day', dayText(row))}
      ${line('semi-major axis', orbitText(row))}
      ${line('primary', primary)}
      ${line('data product', r.credit)}
      ${line('data floor', r.floor)}
      <div class="atlas-mf-reason">${escapeHtml(row.availability.reason)}</div>
    </div>`;
  }

  function rowMarkup(node: Node, depth: number, readingById: Map<string, ManifestReading>): string {
    const row = node.row;
    const id = escapeHtml(row.id);
    const isOpen = expanded.has(row.id);
    const focused = focusedBodyId() === row.id;
    const r = readingById.get(row.id);
    if (!r) return '';
    // `--inject manifest-miswire` mislabels swap[0]'s row with swap[1]'s
    // display name; the title and jump keep the row's real name, so only the
    // rendered label a person reads (and the `label` bar checks) is wrong.
    const swapPair = swap;
    const label = swapPair !== null && row.id === swapPair[0]
      ? rows().find((x) => x.id === swapPair[1])?.display_name ?? row.display_name
      : row.display_name;
    const chip = `<span class="atlas-mf-chip">${escapeHtml(r.chip)}</span>`;
    return `<div class="atlas-mf-row${focused ? ' focused' : ''}" style="--mf-depth:${depth}">
      <button type="button" class="atlas-mf-name" data-mf="${id}"
        title="Jump the camera to ${escapeHtml(row.display_name)}">
        <span class="atlas-mf-label">${escapeHtml(label)}</span>
        <span class="atlas-mf-class">${escapeHtml(classWord(row))}</span>
        ${chip}
      </button>
      <button type="button" class="atlas-mf-more${isOpen ? ' open' : ''}" data-mf-more="${id}"
        aria-expanded="${isOpen ? 'true' : 'false'}"${isOpen ? ` aria-controls="mf-detail-${id}"` : ''}
        title="${isOpen ? 'Hide' : 'Show'} ${escapeHtml(row.display_name)}'s data"><i></i></button>
    </div>${isOpen ? detail(row, depth, r) : ''}${node.children.map((c) => rowMarkup(c, depth + 1, readingById)).join('')}`;
  }

  /** Which control, if any, holds keyboard focus - by the id-bearing attribute a repaint can find it by again. */
  function focusRef(): { attr: string; value: string } | null {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !el.contains(active)) return null;
    for (const attr of ['data-mf', 'data-mf-more', 'data-mf-head']) {
      const value = active.getAttribute(attr);
      if (value !== null) return { attr, value };
    }
    return null;
  }

  function findByAttr(attr: string, value: string): HTMLElement | null {
    for (const node of el.querySelectorAll(`[${attr}]`)) {
      if (node instanceof HTMLElement && node.getAttribute(attr) === value) return node;
    }
    return null;
  }

  function paint(): void {
    const all = rows();
    const key = dynamicKey(all);
    // Nothing displayed can have changed: skip the rebuild entirely rather
    // than build the markup and throw it away, which used to run on every
    // drained wheel tick whether or not the band it draws moved.
    if (painted !== null && cache !== null && cache.rowsRef === all && key === paintedKey) return;
    const built = ensureCache();
    const body = !open
      ? ''
      : all.length === 0
        ? '<div class="atlas-mf-empty">the registry has not landed yet</div>'
        : built.tree.map((n) => rowMarkup(n, 0, built.readingById)).join('');
    const markup = `<div class="atlas-sect${open ? '' : ' closed'}">
      <button type="button" class="atlas-sect-head" data-mf-head="1"
        aria-expanded="${open ? 'true' : 'false'}">BODIES &middot; ${all.length}<i></i></button>
      <div class="atlas-sect-body">${body}</div>
    </div>`;
    paintedKey = key;
    if (markup === painted) return;
    // The scroll position is the reader's place in a 21-row list, and this
    // panel repaints on every focus change.
    const scroller = el.querySelector('.atlas-sect-body');
    const keep = scroller instanceof HTMLElement ? scroller.scrollTop : 0;
    const refocus = focusRef();
    el.innerHTML = markup;
    painted = markup;
    paints++;
    const next = el.querySelector('.atlas-sect-body');
    if (next instanceof HTMLElement) next.scrollTop = keep;
    // `innerHTML` destroyed whatever held focus; a keyboard reader who just
    // opened a row would otherwise fall back to <body> and have to Tab down
    // the whole document to reach the next one.
    if (refocus) findByAttr(refocus.attr, refocus.value)?.focus();
  }

  function onClick(e: MouseEvent): void {
    const target = e.target as HTMLElement | null;
    if (target === null) return;
    const head = target.closest('[data-mf-head]');
    if (head !== null) {
      open = !open;
      remember();
      paint();
      return;
    }
    const more = target.closest('[data-mf-more]') as HTMLElement | null;
    if (more !== null) {
      const id = more.dataset['mfMore'];
      if (id !== undefined) {
        if (expanded.has(id)) expanded.delete(id);
        else expanded.add(id);
        remember();
        paint();
      }
      return;
    }
    const name = target.closest('[data-mf]') as HTMLElement | null;
    const id = name?.dataset['mf'];
    if (id === undefined) return;
    const wired = swap === null ? id : id === swap[0] ? swap[1] : id === swap[1] ? swap[0] : id;
    focus(wired);
  }

  el.addEventListener('click', onClick);

  const handle: BodyManifestHandle = {
    update(): void {
      const show = visible();
      el.classList.toggle('hidden', !show);
      // Nothing to repaint behind a display:none panel, and the markup carries
      // the focused row, which changes on every jump.
      if (show) paint();
    },
    setDebug(next): { swap: readonly string[] | null; paints: number } {
      if (next.swap === true) {
        const ids = flat();
        // Two rows that are not the frame origin, so the control does not aim
        // a jump at the row every other one hangs off.
        const pair = ids.slice(1, 3);
        swap = pair.length === 2 ? [pair[0]!, pair[1]!] : null;
      } else if (next.swap === false) {
        swap = null;
      }
      return { swap, paints };
    },
    dispose(): void {
      el.removeEventListener('click', onClick);
      el.remove();
      painted = null;
      paintedKey = null;
      cache = null;
    },
  };
  handle.update();
  return handle;
}
